import { Accordion, AccordionButton, AccordionItem } from '@reach/accordion';
import cx from 'clsx';
import React, { useState } from 'react';

import { isActive } from '@/components/Header/constants';
import { GameClickProps } from '@/components/modular-grid/types';
import Card from '@/components/NavigationContent/Card/Card';
import {
  CardCategory,
  ExtendedCategoryItem,
} from '@/components/NavigationContent/types';
import SubNavigation from '@/components/SideNavigation/SubNavigation';
import {
  NavigationCategory,
  NavigationCategoryItem,
} from '@/components/SideNavigation/types';

import AnimatedAccordionButton from './Accordion/AnimatedAccordionButton';
import AnimatedAccordionPanel from './Accordion/AnimatedAccordionPanel';
import styles from './MainNavigation.module.scss';
import MainNavigationSlot from './MainNavigationSlot';

import '@reach/accordion/styles.css';

interface MainNavigationPropTypes {
  categories: Array<NavigationCategory | CardCategory>;
  onItemClick: (
    event: React.MouseEvent<{}>,
    item: NavigationCategoryItem | ExtendedCategoryItem
  ) => void;
  onSubMenuItemClick?: (args: { itemTitle?: string; path?: string }) => void;
  onNavigationGameClick?: (args: {
    itemTitle: string;
    game: GameClickProps;
    sectionTitle: string;
    gamePosition: number;
  }) => void;
  onNavigationGameInfoClick?: (args: {
    itemTitle: string;
    game: GameClickProps;
    sectionTitle: string;
    gamePosition: number;
  }) => void;
  onNavigationCommandClick?: (args: {
    itemTitle: string;
    account?: string;
  }) => void;
  visible?: boolean;
  pageTitle?: string;
}

const MainNavigation: React.FC<MainNavigationPropTypes> = ({
  categories,
  onItemClick,
  onSubMenuItemClick,
  onNavigationGameClick,
  onNavigationGameInfoClick,
  onNavigationCommandClick,
  visible = true,
  pageTitle,
}) => {
  const [hoveredItem, setHoveredItem] = useState<string>('');

  if (!visible) return null;
  const handleNavItemClick = (
    e: React.MouseEvent<{}>,
    item: NavigationCategoryItem
  ) => {
    e.preventDefault();
    onItemClick(e, item);
  };

  return (
    <div
      data-testid="sideNavigation"
      id="sideNavigation"
      className={cx(styles.wrapper)}
    >
      {categories.map((category, categoryIdx) => {
        switch (category.type) {
          case 'Navigation - Left': {
            return (
              <Accordion
                key={categoryIdx}
                collapsible
                data-testid="main-nav-wrapper"
                className={styles.accordionWrapper}
              >
                {category.items.map((item, index) => {
                  if (item.url || item.game || item.command) {
                    return (
                      <div
                        key={index}
                        className={cx(
                          styles.slot,
                          !isActive(item.state) && styles.slideRight
                        )}
                        onMouseEnter={() => setHoveredItem(item.title)}
                        onMouseLeave={() => setHoveredItem('')}
                      >
                        <MainNavigationSlot
                          item={item}
                          keyId={index}
                          isHovered={hoveredItem === item.title}
                          onNavigationGameClick={onNavigationGameClick}
                          onNavigationGameInfoClick={onNavigationGameInfoClick}
                          onNavigationCommandClick={onNavigationCommandClick}
                          pageTitle={pageTitle}
                        />
                      </div>
                    );
                  }
                  return (
                    <AccordionItem key={index}>
                      <div
                        className={cx(
                          styles.slot,
                          !isActive(item.state) && styles.slideRight
                        )}
                        onMouseEnter={() => setHoveredItem(item.title)}
                        onMouseLeave={() => setHoveredItem('')}
                      >
                        <div onClick={e => handleNavItemClick(e, item)}>
                          <AccordionButton
                            className={cx(
                              styles.accordionButton,
                              styles.accordionButtonWidth
                            )}
                          >
                            <MainNavigationSlot
                              item={item}
                              key={index}
                              isHovered={hoveredItem === item.title}
                              onNavigationGameClick={onNavigationGameClick}
                              onNavigationCommandClick={
                                onNavigationCommandClick
                              }
                              onNavigationGameInfoClick={
                                onNavigationGameInfoClick
                              }
                              pageTitle={pageTitle}
                            />
                          </AccordionButton>
                        </div>
                        <AnimatedAccordionButton
                          isActive={isActive(item.state)}
                          hideExpandButton={!item.routes?.length}
                        />
                      </div>
                      <AnimatedAccordionPanel>
                        <SubNavigation
                          items={item.routes}
                          onClick={(menuSubItem: {
                            title: string;
                            route_link?: string;
                          }) => {
                            onSubMenuItemClick &&
                              onSubMenuItemClick({
                                path: menuSubItem.route_link || '',
                                itemTitle:
                                  item.tracking_metadata?.title || item.title,
                              });
                          }}
                        />
                      </AnimatedAccordionPanel>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            );
          }
          case 'Card': {
            return (
              <Card
                key={categoryIdx}
                items={category.items}
                handleItemClick={onItemClick}
                title={category.title}
                displayList={true}
              />
            );
          }
          default: {
            return null;
          }
        }
      })}
    </div>
  );
};

export default MainNavigation;
